import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FiBookOpen,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiFilter,
  FiRefreshCcw,
  FiSearch,
  FiUsers,
} from 'react-icons/fi'
import staffTeachingApi from '../../api/staffTeachingApi'
import './Students.css'

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'inactive', label: 'Inactive' },
]

const STATUS_LABELS = {
  active: 'Active',
  pending: 'Pending',
  completed: 'Completed',
  inactive: 'Inactive',
  other: 'Other',
  unknown: 'Unknown',
}

const formatDate = (value) => {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const prettifyStatus = (value) => {
  if (!value) return 'Unknown'
  return value
    .toString()
    .split(/[\s_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const getInitials = (name, fallback = '?') => {
  if (!name) return fallback
  const parts = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
  if (!parts.length) return fallback
  return parts.map((part) => part[0]?.toUpperCase() || '').join('') || fallback
}

const Students = () => {
  const [students, setStudents] = useState([])
  const [batches, setBatches] = useState([])
  const [stats, setStats] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastSynced, setLastSynced] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [batchFilter, setBatchFilter] = useState('all')

  const loadStudents = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const payload = await staffTeachingApi.fetchStudents()
      setStudents(payload?.students || [])
      setBatches(payload?.batches || [])
      setStats(payload?.stats || null)
      setLastSynced(payload?.updatedAt || new Date().toISOString())
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to load students'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStudents()
  }, [loadStudents])

  const batchesOptions = useMemo(() => {
    const base = [{ value: 'all', label: 'All batches' }]
    if (!Array.isArray(batches) || !batches.length) {
      return base
    }
    const options = batches.map((batch) => ({
      value: batch.batchId,
      label: batch.courseTitle
        ? `${batch.batchName} · ${batch.courseTitle}`
        : batch.batchName,
    }))
    return base.concat(options)
  }, [batches])

  const filteredStudents = useMemo(() => {
    if (!Array.isArray(students)) return []
    const query = searchQuery.trim().toLowerCase()

    return students.filter((student) => {
      const studentStatus = student.primaryStatus || 'unknown'
      const matchesStatus =
        statusFilter === 'all' || studentStatus === statusFilter

      const matchesBatch =
        batchFilter === 'all' ||
        student.batches?.some((batch) => batch.batchId === batchFilter)

      const matchesSearch =
        !query ||
        [
          student.firstName,
          student.lastName,
          student.email,
          student.phone,
          ...(student.batches || []).map(
            (batch) => `${batch.batchName || ''} ${batch.courseTitle || ''}`
          ),
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))

      return matchesStatus && matchesBatch && matchesSearch
    })
  }, [students, statusFilter, batchFilter, searchQuery])

  const statsItems = useMemo(() => {
    const summary = stats || {}
    return [
      {
        label: 'Total students',
        value: summary.totalStudents ?? students.length,
        icon: FiUsers,
      },
      {
        label: 'Active enrollments',
        value: summary.activeEnrollments ?? 0,
        icon: FiCheckCircle,
      },
      {
        label: 'Pending approvals',
        value: summary.pendingEnrollments ?? 0,
        icon: FiClock,
      },
      {
        label: 'Assigned batches',
        value: summary.assignedBatchCount ?? batches.length,
        icon: FiBookOpen,
      },
    ]
  }, [stats, students.length, batches.length])

  const lastSyncedLabel = lastSynced
    ? new Date(lastSynced).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'Not synced yet'

  const emptyStateMessage = searchQuery
    ? 'No students match that search query.'
    : 'No students found for your assigned batches yet.'

  return (
    <div className="students-page">
      <header className="students-hero">
        <div>
          <p className="eyebrow">Student management</p>
          <h1>My Students</h1>
          <p className="subhead">View and manage students across all your batches</p>
        </div>
        <div className="students-actions">
          <button
            type="button"
            className="refresh-button"
            onClick={loadStudents}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            {isLoading ? 'Refreshing…' : 'Refresh roster'}
          </button>
          <span className="last-synced">Last synced · {lastSyncedLabel}</span>
        </div>
      </header>

      <section className="students-stats">
        {statsItems.map((item) => {
          const Icon = item.icon
          return (
            <article key={item.label} className="stat-card">
              <span className="stat-icon">
                <Icon size={18} />
              </span>
              <div>
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </article>
          )
        })}
      </section>

      <section className="students-toolbar">
        <label className="students-search">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search by name, email, or batch"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
        <div className="students-filters">
          <div className="select-wrapper">
            <FiFilter size={16} />
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="select-wrapper">
            <FiBookOpen size={16} />
            <select
              value={batchFilter}
              onChange={(event) => setBatchFilter(event.target.value)}
            >
              {batchesOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {error && (
        <div className="students-error" role="alert">
          {error}
        </div>
      )}

      {isLoading && !students.length ? (
        <div className="students-state">Loading student roster…</div>
      ) : filteredStudents.length === 0 ? (
        <div className="students-state">{emptyStateMessage}</div>
      ) : (
        <section className="students-table-wrapper" aria-live="polite">
          <table className="students-table">
            <thead>
              <tr>
                <th scope="col">Student</th>
                <th scope="col">Contact</th>
                <th scope="col">Status</th>
                <th scope="col">Batches</th>
                <th scope="col">Last joined</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => {
                const name = [student.firstName, student.lastName]
                  .filter(Boolean)
                  .join(' ')
                const displayName = name || student.email || 'Unnamed student'
                const studentStatus = student.primaryStatus || 'unknown'
                const batchesList = Array.isArray(student.batches)
                  ? student.batches
                  : []
                const displayedBatches = batchesList.slice(0, 2)
                const overflowCount = Math.max(batchesList.length - 2, 0)

                return (
                  <tr key={student.studentId}>
                    <td>
                      <div className="student-ident">
                        <div className="student-avatar">
                          {student.avatarUrl ? (
                            <img
                              src={student.avatarUrl}
                              alt={displayName}
                              loading="lazy"
                            />
                          ) : (
                            <span>{getInitials(displayName)}</span>
                          )}
                        </div>
                        <div>
                          <strong>{displayName}</strong>
                          <span className="student-email">{student.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="student-contact">
                        <span>{student.email}</span>
                        {student.phone && <span>{student.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill status-${studentStatus}`}>
                        {STATUS_LABELS[studentStatus] || STATUS_LABELS.unknown}
                      </span>
                    </td>
                    <td>
                      <div className="student-batches">
                        {displayedBatches.map((batch) => (
                          <div
                            key={`${student.studentId}-${batch.batchId || 'batch'}-${batch.enrollmentStatus}`}
                            className="batch-chip"
                          >
                            <span>{batch.batchName}</span>
                            <span
                              className={`batch-status status-${
                                (batch.enrollmentStatus || 'unknown').toLowerCase()
                              }`}
                            >
                              {prettifyStatus(batch.enrollmentStatus)}
                            </span>
                          </div>
                        ))}
                        {overflowCount > 0 && (
                          <span className="batch-overflow">+{overflowCount} more</span>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(student.lastJoinedAt)}</td>
                    <td className="student-actions">
                      <Link
                        to={`/students/${student.studentId}`}
                        className="student-link"
                      >
                        View profile <FiChevronRight size={14} />
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}

export default Students
